/**
 * OnboardingContext — Shared state between onboarding steps (A10-A12b)
 *
 * Reads workspace_name and workspace_tipo from localStorage (set during
 * CreateWorkspace A05). Stores fiscal data, team invites, vertical choice,
 * and selected modules so each step can read/write them.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

export interface TeamInvite {
  email: string;
  nombre: string;
  rol: string;
}

export type VerticalId = 'tapiceria' | 'celulares' | 'construccion' | 'retail' | 'otro';

export interface FiscalData {
  logoFile: File | null;
  logoPreview: string | null;
  razonSocial: string;
  rnc: string;
  tipoContribuyente: 'persona-juridica' | 'persona-fisica';
  direccion: string;
  ciudad: string;
  provincia: string;
  codigoPostal: string;
  telefono: string;
}

export interface OnboardingState {
  /** Workspace name from localStorage (set during A05) */
  workspaceName: string;
  /** Workspace tipo from localStorage (set during A05) */
  workspaceTipo: string;
  /** Empresa ID from localStorage (set during A05) */
  empresaId: string;

  fiscal: FiscalData;
  teamInvites: TeamInvite[];
  vertical: VerticalId | null;
  /** Module keys that user toggled on */
  selectedModules: Record<string, boolean>;
}

interface OnboardingContextType {
  state: OnboardingState;
  setFiscal: (data: Partial<FiscalData>) => void;
  setTeamInvites: (invites: TeamInvite[]) => void;
  setVertical: (v: VerticalId | null) => void;
  setSelectedModules: (modules: Record<string, boolean>) => void;
}

// ── Defaults ─────────────────────────────────────────────────────────────────

const defaultFiscal: FiscalData = {
  logoFile: null,
  logoPreview: null,
  razonSocial: '',
  rnc: '',
  tipoContribuyente: 'persona-juridica',
  direccion: '',
  ciudad: '',
  provincia: '',
  codigoPostal: '',
  telefono: '',
};

const defaultModules: Record<string, boolean> = {
  crm: true,
  cotizaciones: true,
  ordenes: true,
  inventario: true,
  facturacion: true,
  notificaciones: true,
  produccion: false,
  logistica: false,
  cobros: false,
  rrhh: false,
  contabilidad: false,
  proveedores: false,
};

// ── Context ──────────────────────────────────────────────────────────────────

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider');
  return ctx;
};

// ── Provider ─────────────────────────────────────────────────────────────────

export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [workspaceName] = useState(() => localStorage.getItem('workspace_name') || '');
  const [workspaceTipo] = useState(() => localStorage.getItem('workspace_tipo') || '');
  const [empresaId] = useState(() => localStorage.getItem('empresa_id') || '');

  const [fiscal, setFiscalState] = useState<FiscalData>(() => ({
    ...defaultFiscal,
    razonSocial: localStorage.getItem('workspace_name') || '',
  }));

  const [teamInvites, setTeamInvites] = useState<TeamInvite[]>([
    { email: '', nombre: '', rol: '' },
  ]);

  const [vertical, setVertical] = useState<VerticalId | null>(null);
  const [selectedModules, setSelectedModules] = useState<Record<string, boolean>>(defaultModules);

  const setFiscal = (data: Partial<FiscalData>) => {
    setFiscalState(prev => ({ ...prev, ...data }));
  };

  return (
    <OnboardingContext.Provider
      value={{
        state: {
          workspaceName,
          workspaceTipo,
          empresaId,
          fiscal,
          teamInvites,
          vertical,
          selectedModules,
        },
        setFiscal,
        setTeamInvites,
        setVertical,
        setSelectedModules,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
